import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { MultiSelectFilter } from "components/DesignSystem/Filter/Filter";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ArrowDown } from "components/icons/ArrowDown";
import { AnimatePresence } from "framer-motion";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useMemo } from "react";
import { useGetAssignAgentsQuery } from "store/apis/agents";

export const AssignAgentFilter = ({
  selectedAgents,
  setSelectedAgents,
}: {
  selectedAgents: string[];
  setSelectedAgents: (val: string[]) => void;
}) => {
  const { isAdmin } = useRoleBasedView();
  const { data: assignAgents = [] } = useGetAssignAgentsQuery(
    {},
    { skip: !isAdmin }
  );
  const { isOpen, toggle } = useModal();

  const agentOptions = useMemo(
    () =>
      assignAgents.map((assignee) => ({
        value: assignee.profile_id,
        label: assignee.name,
      })),
    [assignAgents]
  );

  const selectedLabels = useMemo(() => {
    return agentOptions
      .filter((option) => selectedAgents.includes(option.value))
      .map((option) => option.label);
  }, [agentOptions, selectedAgents]);

  const hasSelection = selectedLabels.length > 0;
  const displayText = hasSelection ? selectedLabels.join(", ") : "Select";
  const tooltipText = selectedLabels.join(", ");

  return (
    <Dropdown.Root open={isOpen} onOpenChange={toggle}>
      <ConditionalToolTip
        text={tooltipText}
        condition={hasSelection}
        propClass="t-max-w-64"
      >
        <Dropdown.Trigger asChild>
          <button
            type="button"
            className="all:unset t-flex t-items-center t-gap-2 t-px-3 t-py-1.5 t-border t-border-solid t-border-surface-grey t-rounded-lg t-bg-surface-lighter-grey hover:t-border-purple-30 hover:t-bg-neutral-0 t-cursor-pointer t-w-36"
          >
            <span className="t-truncate t-text-body-sm t-flex-1 t-text-left">
              {displayText}
            </span>
            <ArrowDown strokeWidth="1.5" color="currentColor" />
          </button>
        </Dropdown.Trigger>
      </ConditionalToolTip>
      <Dropdown.Portal>
        <AnimatePresence>
          {isOpen && (
            <Dropdown.Content asChild sideOffset={8} align="end">
              <div className="t-max-h-64 t-overflow-auto">
                <MultiSelectFilter
                  isSearchable={true}
                  isSelectAll={true}
                  onChange={setSelectedAgents}
                  checkboxSpace="small"
                  options={agentOptions}
                  selected={selectedAgents}
                  className="t-p-2"
                />
              </div>
            </Dropdown.Content>
          )}
        </AnimatePresence>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};
